import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import {
    Box,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    TablePagination,
} from '@mui/material';
import { GridFilterModel, GridSortItem, GridSortModel } from '@mui/x-data-grid';
import { Device, NapiResultType } from 'types';
import { devicePanelViewColumns } from 'data-structures/sharedColumns/devicePanelViewColumns';
import { DataTable } from 'components/data/DataTable/DataTable';
import { TankLevelIndicator } from 'components/data/TankLevelIndicator/TankLevelIndicator';
import styles from './TransformingTanksList.module.scss';
import DaysUntilRecommendedFill from 'components/data/DaysUntilRecommendedFill/DaysUntilRecommendedFill';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { useAuth, useUserSession } from 'hooks';
import { updateUserSession } from 'lib/firebase';
import { UserAccountsSession } from 'types/User/firebaseTypes';

interface TransformingTanksListProps {
    data: NapiResultType<Device[]>;
    loading: boolean;
    fetching: boolean;
    columnsEnabled?: boolean;
    onDeviceSelected?: (deviceId: string, device: Device) => void;
    onFilterModelChange?: Dispatch<SetStateAction<GridFilterModel | undefined>>;
    onPageChange?: Dispatch<SetStateAction<number>>;
    onPageSizeChange?: Dispatch<SetStateAction<number>>;
    onSortModelChange?: Dispatch<SetStateAction<GridSortItem[]>>;
    page: number;
    sortModel?: GridSortModel;
    selectedDeviceId: string;
}

export const TransformingTanksList: FC<TransformingTanksListProps> = ({
    data,
    loading,
    fetching,
    columnsEnabled,
    onDeviceSelected,
    onFilterModelChange,
    onPageChange,
    onPageSizeChange,
    onSortModelChange,
    page,
    sortModel,
    selectedDeviceId,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [],
    });
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const listRefs: { [id: string]: HTMLDivElement | null } = {};

    const handleListClick = (deviceId: string, device: Device) => {
        if (!!onDeviceSelected) onDeviceSelected(deviceId, device);
    };
    const search = (searchTerm: string) => {
        const filter = {
            quickFilterValues: [searchTerm],
            items: [],
        };
        setFilterModel(filter);
        if (!!onFilterModelChange) onFilterModelChange(filter);
    };
    const changePage = (page: number) => {
        if (!!onPageChange) onPageChange(page);
    };
    const changeRowsPerPage = (rowsPerPage: number) => {
        setRowsPerPage(rowsPerPage);
        if (!!onPageSizeChange) onPageSizeChange(rowsPerPage);
    };
    const isElementInView = (element: HTMLDivElement) => {
        const rect = element.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <=
                (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <=
                (window.innerWidth || document.documentElement.clientWidth)
        );
    };
    const session = useUserSession();
    const { user } = useAuth();

    useEffect(() => {
        if (session?.accounts) {
            onSortModelChange &&
                onSortModelChange([
                    {
                        field: session.accounts.sort.field,
                        sort: session.accounts.sort.direction,
                    },
                ]);
        }
    }, [session, onSortModelChange]);

    useEffect(() => {
        if (columnsEnabled || !selectedDeviceId) return;
        const ref = listRefs[selectedDeviceId];
        if (!!ref && !isElementInView(ref)) ref.scrollIntoView();
    });

    const sortOptions = [
        { field: 'lastReading.tank', label: 'Last Reading Level' },
        { field: 'lastReading.time', label: 'Last Reading Time' },
        { field: 'name', label: 'Monitor Name' },
        { field: 'customer_name', label: 'Customer' },
        { field: 'address', label: 'Address' },
    ];

    return (
        <div
            className={styles.TransformingTanksList}
            data-testid="TransformingTanksList"
        >
            {columnsEnabled && (
                <DataTable
                    id="miniDevice"
                    data={data}
                    columns={devicePanelViewColumns}
                    loading={loading || fetching}
                    selectableListMode={true}
                    getRowId={(device) => device.deviceId}
                    hideToolbar={true}
                    onRowClick={(params) =>
                        handleListClick(params.row.deviceId, params.row)
                    }
                    onFilterModelChange={(params) => {
                        const value = params.quickFilterValues?.[0];
                        setSearchTerm(value);
                        search(value);
                    }}
                    filterModel={filterModel}
                    onPageChange={(params) => {
                        const value = params.valueOf();
                        if (typeof value === 'number') changePage(value);
                        if (!!onPageChange) onPageChange(params);
                    }}
                    onPageSizeChange={(params) => {
                        const value = params.valueOf();
                        if (typeof value === 'number') changeRowsPerPage(value);
                        if (!!onPageSizeChange) onPageSizeChange(params);
                    }}
                    onSortModelChange={onSortModelChange}
                    filterMode="server"
                    paginationMode="server"
                    sortingMode="server"
                    page={page}
                    pageSize={rowsPerPage}
                    autoFocusSearch={!!searchTerm}
                    sortModel={sortModel}
                    disableColumnMenu
                />
            )}
            {!columnsEnabled && (
                <div className={styles.enhancedList}>
                    <div className={styles.tankListToolbar}>
                        <Select
                            className={styles.sortFieldSelect}
                            value={sortModel?.[0]?.field}
                            onChange={(e) =>
                                onSortModelChange &&
                                onSortModelChange((prevSort) => {
                                    const prevDir = prevSort?.[0]?.sort;

                                    if (user?.uid) {
                                        const newAcctSession: UserAccountsSession =
                                            {
                                                ...session?.accounts,
                                                sort: {
                                                    direction: prevDir,
                                                    field: e.target.value,
                                                },
                                            };
                                        updateUserSession(
                                            user.uid,
                                            'accounts',
                                            newAcctSession
                                        );
                                    }

                                    return [
                                        {
                                            field: e.target.value,
                                            sort: prevDir,
                                        },
                                    ];
                                })
                            }
                            variant="standard"
                        >
                            {sortOptions.map((opt) => {
                                return (
                                    <MenuItem
                                        value={opt.field}
                                        key={`sort_option_${opt.field}`}
                                    >
                                        {opt.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        <IconButton
                            onClick={() =>
                                onSortModelChange &&
                                onSortModelChange((prevSort) => {
                                    const prevDir = prevSort?.[0]?.sort;
                                    const prevField = prevSort?.[0]?.field;
                                    const newDir =
                                        prevDir === 'asc' ? 'desc' : 'asc';

                                    if (user?.uid) {
                                        const newAcctSession: UserAccountsSession =
                                            {
                                                ...session?.accounts,
                                                sort: {
                                                    direction: newDir,
                                                    field: prevField,
                                                },
                                            };
                                        updateUserSession(
                                            user.uid,
                                            'accounts',
                                            newAcctSession
                                        );
                                    }

                                    return [{ field: prevField, sort: newDir }];
                                })
                            }
                        >
                            {sortModel?.[0]?.sort === 'asc' ? (
                                <ArrowUpward />
                            ) : (
                                <ArrowDownward />
                            )}
                        </IconButton>
                    </div>
                    <List className={styles.mainList}>
                        {data.results.map((device) => (
                            <ListItemButton
                                ref={(item) =>
                                    (listRefs[device.deviceId] = item)
                                }
                                key={device.deviceId}
                                selected={selectedDeviceId === device.deviceId}
                                onClick={(_event) =>
                                    handleListClick(device.deviceId, device)
                                }
                            >
                                <ListItemIcon>
                                    <TankLevelIndicator
                                        tankLevelPercentage={
                                            device.lastReading?.tank
                                        }
                                        gallons={device.gallons}
                                    />
                                </ListItemIcon>
                                <Box
                                    className={
                                        styles.deviceInformationContainer
                                    }
                                >
                                    <ListItemText
                                        primary={
                                            device.customer_name ??
                                            device.shortDeviceId
                                        }
                                        secondary={
                                            device.address ??
                                            'No Address Entered'
                                        }
                                    />

                                    <div className={styles.duf7Container}>
                                        <span>Days Until Next Fill: </span>
                                        <DaysUntilRecommendedFill
                                            duf_7={device.duf_7}
                                        />
                                    </div>
                                </Box>
                            </ListItemButton>
                        ))}
                    </List>
                    <TablePagination
                        component="div"
                        count={data.total}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(_event, page) => changePage(page)}
                        onRowsPerPageChange={(event) =>
                            changeRowsPerPage(parseInt(event.target.value))
                        }
                    />
                </div>
            )}
        </div>
    );
};
